import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  BedDouble,
  CalendarDays,
  ExternalLink,
  Loader2,
  Lock,
  MapPin,
  Sparkles,
  Ticket,
  UtensilsCrossed,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  createOriginalCheckoutSession,
  getOriginalPreview,
  getPurchasedOriginal,
  setOriginalPurchaseHidden,
  type OriginalRouteCard,
  type PurchasedOriginal,
} from "@/lib/originals.functions";
import {
  areaMapsUrl,
  formatOriginalPrice,
  stopMapsUrl,
  type OriginalBase,
  type OriginalRoutePayload,
} from "@/lib/originals";
import { normalizeTransportMode } from "@/lib/originals-directions";
import { OriginalMapsButtons } from "@/components/OriginalMapsButtons";
import { OriginalBuyerTools } from "@/components/OriginalBuyerTools";
import { loadOfflineOriginal, saveOfflineOriginal } from "@/lib/original-exports";
import {
  getSessionSource,
  recordOriginalEventOncePerSession,
  recordOriginalViewOncePerSession,
} from "@/lib/originals-view-hook";

export const Route = createFileRoute("/originals/$slug")({
  component: OriginalDetailPage,
  head: () => ({
    meta: [
      { title: "Rota pronta — Routify Originals" },
      {
        name: "description",
        content:
          "Vê o primeiro dia desta rota Routify Original e desbloqueia o roteiro completo com dicas e alternativas.",
      },
      { property: "og:title", content: "Rota pronta — Routify Originals" },
      {
        property: "og:description",
        content: "Roteiro completo feito pela equipa Routify, com dicas próprias e alternativas.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

function basesForDay(bases: OriginalBase[] | undefined, day: number): OriginalBase[] {
  return (bases ?? []).filter((base) => base.day == null || base.day === day);
}

function BaseList({
  title,
  icon,
  bases,
}: {
  title: string;
  icon: React.ReactNode;
  bases: OriginalBase[];
}) {
  if (bases.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-border/70 bg-muted/30 p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        {icon} {title}
      </p>
      <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
        {bases.map((base) => (
          <li key={`${base.label}-${base.day ?? "all"}`}>
            <a
              href={areaMapsUrl(base.label, base.lat, base.lng)}
              target="_blank"
              rel="noreferrer"
              className="text-brand underline-offset-2 hover:underline"
            >
              {base.label}
            </a>
            {base.note ? ` — ${base.note}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DaysList({
  payload,
  locked,
}: {
  payload: OriginalRoutePayload;
  locked: boolean;
}) {
  const routeMode = normalizeTransportMode(payload.transportMode);
  return (
    <div className="space-y-6">
      {payload.days.map((day) => {
        const dayMode = day.transportMode
          ? normalizeTransportMode(day.transportMode)
          : routeMode;
        return (
          <section key={day.day} className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-base font-semibold">
              Dia {day.day}
              {day.title ? ` — ${day.title}` : ""}
            </h3>
            {day.summary && <p className="mt-1 text-sm text-muted-foreground">{day.summary}</p>}
            {!locked && <OriginalMapsButtons stops={day.stops} mode={dayMode} />}
            {day.stops.length === 0 ? (
              <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Lock className="h-4 w-4" /> Disponível depois da compra.
              </p>
            ) : (
              <ol className="mt-3 space-y-3">
                {day.stops.map((stop) => (
                  <li key={stop.id} className="border-l-2 border-brand/40 pl-3">
                    <p className="font-medium">
                      {stop.suggestedTime ? `${stop.suggestedTime} · ` : ""}
                      {stop.name}
                    </p>
                    {stop.description && (
                      <p className="text-sm text-muted-foreground">{stop.description}</p>
                    )}
                    {stop.tip && (
                      <p className="mt-1 text-sm text-brand">Dica Routify: {stop.tip}</p>
                    )}
                    {!locked && (
                      <>
                        <a
                          href={stopMapsUrl(stop)}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-xs text-brand"
                        >
                          <MapPin className="h-3 w-3" /> Abrir no Maps
                        </a>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {stop.alternatives?.length
                            ? `Plano B: ${stop.alternatives.map((a) => a.name).join(" · ")}`
                            : "Sem alternativa preparada para esta paragem."}
                        </p>
                      </>
                    )}
                  </li>
                ))}
              </ol>
            )}
            {!locked && (
              <>
                <BaseList
                  title="Onde comer"
                  icon={<UtensilsCrossed className="h-4 w-4 text-brand" />}
                  bases={basesForDay(payload.mealBases, day.day)}
                />
                <BaseList
                  title="Onde dormir"
                  icon={<BedDouble className="h-4 w-4 text-brand" />}
                  bases={basesForDay(payload.sleepBases, day.day)}
                />
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

function SalesSections({ sales }: { sales: NonNullable<OriginalRoutePayload["sales"]> }) {
  const facts = [
    ["Ponto de partida", sales.startPoint],
    ["Ritmo", sales.pace],
    ["Esforço a pé", sales.walkingEffort],
  ].filter(([, value]) => !!value) as [string, string][];

  return (
    <div className="mt-6 space-y-4">
      {(sales.audience || sales.notFor) && (
        <section className="rounded-xl border border-border bg-card p-4">
          {sales.audience && (
            <>
              <h2 className="text-base font-semibold">Para quem é esta rota</h2>
              <p className="mt-1 text-sm text-muted-foreground">{sales.audience}</p>
            </>
          )}
          {sales.notFor && (
            <p className="mt-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Talvez não seja para ti: </span>
              {sales.notFor}
            </p>
          )}
        </section>
      )}

      {facts.length > 0 && (
        <section className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-3">
          {facts.map(([label, value]) => (
            <div key={label}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="mt-0.5 text-sm">{value}</p>
            </div>
          ))}
        </section>
      )}

      {(sales.included?.length || sales.notIncluded?.length) && (
        <section className="grid gap-4 sm:grid-cols-2">
          {sales.included?.length ? (
            <div className="rounded-xl border border-border bg-card p-4">
              <h2 className="text-base font-semibold">O que recebes</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {sales.included.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {sales.notIncluded?.length ? (
            <div className="rounded-xl border border-border bg-card p-4">
              <h2 className="text-base font-semibold">O que fica por tua conta</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {sales.notIncluded.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      )}

      {(sales.preparedBy || sales.reviewedOn) && (
        <p className="text-xs text-muted-foreground">
          {sales.preparedBy ? `Preparada por ${sales.preparedBy}.` : ""}
          {sales.reviewedOn ? ` Última revisão em ${sales.reviewedOn}.` : ""}
        </p>
      )}
    </div>
  );
}

function OriginalDetailPage() {
  const { slug } = Route.useParams();

  useEffect(() => {
    recordOriginalViewOncePerSession(slug);
  }, [slug]);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const previewFn = useServerFn(getOriginalPreview);
  const purchasedFn = useServerFn(getPurchasedOriginal);
  const checkoutFn = useServerFn(createOriginalCheckoutSession);
  const setHiddenFn = useServerFn(setOriginalPurchaseHidden);

  const [card, setCard] = useState<OriginalRouteCard | null>(null);
  const [payload, setPayload] = useState<OriginalRoutePayload | null>(null);
  const [hiddenStops, setHiddenStops] = useState(0);
  const [owned, setOwned] = useState(false);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [hiddenFromList, setHiddenFromList] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [offlineSavedAt, setOfflineSavedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let purchased: PurchasedOriginal | null = null;
      if (isAuthenticated) {
        purchased = await purchasedFn({ data: { slug } });
      }
      if (purchased) {
        setCard(purchased.route);
        setPayload(purchased.payload);
        setOwned(true);
        setPurchaseId(purchased.purchaseId);
        setHiddenFromList(purchased.hidden);
        setHiddenStops(0);
        saveOfflineOriginal(purchased.route, purchased.payload);
        setOfflineSavedAt(new Date().toISOString());
        return;
      }
      const preview = await previewFn({ data: { slug } });
      if (!preview) {
        setNotFound(true);
        return;
      }
      setCard(preview.route);
      setPayload(preview.preview);
      setHiddenStops(preview.preview.hiddenStops);
      setOwned(false);
    } catch {
      // Sem rede: abre a cópia guardada neste dispositivo (só existe se comprou).
      const cached = loadOfflineOriginal<OriginalRouteCard>(slug);
      if (cached) {
        setCard(cached.card);
        setPayload(cached.payload);
        setOwned(true);
        setHiddenStops(0);
        setOfflineSavedAt(cached.savedAt);
        toast.message("Sem ligação: a mostrar a cópia guardada neste dispositivo.");
      } else {
        toast.error("Não foi possível abrir esta rota.");
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, previewFn, purchasedFn, slug]);

  useEffect(() => {
    if (authLoading) return;
    void load();
  }, [authLoading, load]);

  async function handleBuy() {
    // Contar nunca pode impedir entrar na conta ou pagar.
    try {
      recordOriginalEventOncePerSession(slug, "buy_click");
    } catch {
      // ignorado de propósito
    }
    if (!isAuthenticated) {
      storePostLoginRedirect(`/originals/${slug}`);
      void navigate({ to: "/login" });
      return;
    }
    setBuying(true);
    try {
      const result = await checkoutFn({
        data: {
          slug,
          returnUrl: `${window.location.origin}/originals/${slug}`,
          environment: getStripeEnvironment(),
          source: getSessionSource(),
        },
      });
      if ("error" in result && result.error) {
        toast.error(result.error);
        return;
      }
      if ("url" in result && result.url) {
        window.location.href = result.url;
        return;
      }
      toast.error("Não foi possível abrir o pagamento.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível abrir o pagamento.");
    } finally {
      setBuying(false);
    }
  }

  if (loading) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-10">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> A carregar rota…
        </div>
      </main>
    );
  }

  if (notFound || !card || !payload) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-10">
        <p className="text-muted-foreground">Esta rota não existe ou já não está à venda.</p>
        <Link to="/originals" className="mt-4 inline-block text-brand">
          Ver todas as rotas
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8 pb-32 sm:pb-8">
      <Link
        to="/originals"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Routify Originals
      </Link>

      {card.heroImageUrl && (
        <img
          src={card.heroImageUrl}
          alt={`Capa da rota ${card.title}`}
          className="mb-5 h-52 w-full rounded-xl object-cover"
        />
      )}

      <h1 className="text-2xl font-bold tracking-tight">{card.title}</h1>
      {card.subtitle && <p className="mt-1 text-muted-foreground">{card.subtitle}</p>}
      <div className="mt-3 flex flex-wrap gap-3 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-4 w-4" /> {card.destination}
        </span>
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="h-4 w-4" /> {card.days} dia{card.days === 1 ? "" : "s"}
        </span>
        <span>{card.stopsCount} paragens</span>
      </div>

      {payload.intro && <p className="mt-4 leading-relaxed">{payload.intro}</p>}

      {payload.gallery && payload.gallery.length > 0 && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {payload.gallery.map((image) => (
            <figure key={image.url}>
              <img
                src={image.url}
                alt={image.alt}
                loading="lazy"
                className="h-44 w-full rounded-xl object-cover"
              />
              {image.caption && (
                <figcaption className="mt-1 text-xs text-muted-foreground">
                  {image.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}

      {payload.sales && <SalesSections sales={payload.sales} />}



      {owned ? (
        <div className="mt-5 space-y-2">
          <p className="inline-flex items-center gap-2 rounded-lg border border-brand/40 bg-brand/5 px-3 py-2 text-sm text-brand">
            <Sparkles className="h-4 w-4" />
            {hiddenFromList
              ? "Esta rota é tua, mas está escondida de As minhas rotas."
              : "Esta rota é tua. Fica guardada em As minhas rotas."}
          </p>
          {hiddenFromList && purchaseId && (
            <button
              type="button"
              disabled={restoring}
              onClick={async () => {
                setRestoring(true);
                try {
                  await setHiddenFn({ data: { purchaseId, hidden: false } });
                  setHiddenFromList(false);
                  toast.success("Rota reposta em As minhas rotas");
                } catch {
                  toast.error("Não foi possível repor a rota.");
                } finally {
                  setRestoring(false);
                }
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm disabled:opacity-60"
            >
              {restoring && <Loader2 className="h-4 w-4 animate-spin" />}
              Repor em As minhas rotas
            </button>
          )}
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-border bg-card p-4">
          <p className="text-sm text-muted-foreground">
            Estás a ver o início da rota. Faltam {hiddenStops} paragens, as dicas de cada dia e os
            planos B.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-xl font-semibold">
              {formatOriginalPrice(card.priceCents, card.currency)}
            </span>
            <button
              type="button"
              onClick={handleBuy}
              disabled={buying}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-60"
            >
              {buying && <Loader2 className="h-4 w-4 animate-spin" />}
              Comprar esta rota
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Pagamento único. Fica na tua conta e podes voltar sempre que quiseres.
          </p>
        </div>
      )}

      {owned && <OriginalBuyerTools card={card} payload={payload} offlineSavedAt={offlineSavedAt} />}

      <div className="mt-8">
        <DaysList payload={payload} locked={!owned} />
      </div>

      {payload.tips && payload.tips.length > 0 && (
        <section className="mt-8 rounded-xl border border-border p-4">
          <h2 className="text-base font-semibold">Conselhos da equipa</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {payload.tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>
      )}

      {owned && payload.practicalLinks && payload.practicalLinks.length > 0 && (
        <section className="mt-8 rounded-xl border border-border p-4">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Ticket className="h-4 w-4 text-brand" /> Links úteis
          </h2>
          <ul className="mt-2 space-y-2 text-sm">
            {payload.practicalLinks.map((link) => (
              <li key={link.url}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand underline-offset-2 hover:underline"
                >
                  {link.label} <ExternalLink className="h-3 w-3" />
                </a>
                {link.note && (
                  <span className="text-muted-foreground"> — {link.note}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!owned && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 backdrop-blur sm:hidden"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div className="mx-auto flex max-w-3xl items-center gap-3">
            <span className="text-lg font-semibold">
              {formatOriginalPrice(card.priceCents, card.currency)}
            </span>
            <button
              type="button"
              onClick={handleBuy}
              disabled={buying}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-brand-foreground disabled:opacity-60"
            >
              {buying && <Loader2 className="h-4 w-4 animate-spin" />}
              Comprar esta rota
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

